import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Phone, ShieldCheck, Home, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";

export default function SignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "broker" ? "broker" : "homeowner";

  // step: 'role' | 'phone' | 'otp'
  const [step, setStep] = useState("role");
  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const otpRef = useRef(null);

  const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailOk = emailRx.test(email.trim());

  useEffect(() => {
    document.title = "Sign in · Zumroo";
  }, []);

  const formatPhone = (raw) => raw.replace(/[^\d]/g, "").slice(0, 10);

  // === Direct REST SendOTP flow — no captcha, OTP arrives directly via SMS ===
  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return toast.error("Enter your name");
    if (!emailOk) return toast.error("Enter a valid email (e.g. you@example.com)");
    if (phone.length !== 10) return toast.error("Enter a valid 10-digit Indian mobile");
    try {
      setLoading(true);
      const { data } = await api.post("/auth/send-otp", { phone: `+91${phone}` });
      setStep("otp");
      setTimeout(() => otpRef.current?.focus(), 100);
      if (data.provider === "msg91") {
        toast.success("OTP sent to your phone via SMS");
      } else {
        toast.success("OTP sent (dev mode — any 4-digit code works)");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 4) return toast.error("Enter the 4-digit OTP");
    try {
      setLoading(true);
      const { data } = await api.post("/auth/verify-otp", {
        phone: `+91${phone}`,
        otp,
        role,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      const token = data.token || data.access_token;
      if (!token) throw new Error("No token in response");
      localStorage.setItem("zumroo_user_token", token);
      localStorage.setItem("zumroo_user", JSON.stringify(data.user || {}));
      // Track last-active for the 5-day idle-logout policy
      localStorage.setItem("zumroo_last_active", String(Date.now()));
      toast.success("Welcome to Zumroo!");
      navigate("/listings", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = () => sendOtp();

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex flex-col">
      {/* Top bar */}
      <div className="container-z py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1A231E] hover:text-[#C86646] transition">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#C86646]" />
          <img src={LOGO_URL} alt="Zumroo" className="h-8 w-8 rounded-lg" />
        </Link>
        <div className="w-12" />
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="zcard w-full max-w-md p-7 sm:p-9"
          data-testid="signin-card"
        >
          {step === "role" && (
            <>
              <div className="label-mono mb-3">Get started</div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231E]">
                Choose how you'll use Zumroo
              </h1>
              <p className="text-sm text-[#5C6B62] mt-2">
                We'll use this to show you the right experience.
              </p>

              <div className="mt-6 space-y-3">
                <button
                  type="button"
                  onClick={() => setRole("homeowner")}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border transition text-left ${
                    role === "homeowner"
                      ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                      : "bg-white border-[#E4E1D9] text-[#1A231E] hover:border-[#1E3F33]"
                  }`}
                  data-testid="role-card-homeowner"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    role === "homeowner" ? "bg-white/15 text-white" : "bg-[#EAF1ED] text-[#1E3F33]"
                  }`}>
                    <Home className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">I'm a Home Owner</div>
                    <div className={`text-sm ${role === "homeowner" ? "text-white/70" : "text-[#5C6B62]"}`}>
                      List for free, get verified buyers
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setRole("broker")}
                  className={`w-full flex items-center gap-4 p-5 rounded-2xl border transition text-left ${
                    role === "broker"
                      ? "bg-[#1E3F33] border-[#1E3F33] text-white"
                      : "bg-white border-[#E4E1D9] text-[#1A231E] hover:border-[#1E3F33]"
                  }`}
                  data-testid="role-card-broker"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    role === "broker" ? "bg-white/15 text-white" : "bg-[#FBEFE9] text-[#C86646]"
                  }`}>
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">I'm a Broker</div>
                    <div className={`text-sm ${role === "broker" ? "text-white/70" : "text-[#5C6B62]"}`}>
                      Unlock owner contacts for ₹49 / ₹79
                    </div>
                  </div>
                </button>
              </div>

              <button
                onClick={() => setStep("phone")}
                className="btn-primary w-full justify-center mt-6"
                data-testid="role-continue-btn"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}

          {step === "phone" && (
            <form onSubmit={sendOtp}>
              <button
                type="button"
                onClick={() => setStep("role")}
                className="text-sm text-[#5C6B62] hover:text-[#1A231E] flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Change role
              </button>
              <div className="label-mono mb-2">Step 1 of 2</div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231E]">
                What's your phone?
              </h1>
              <p className="text-sm text-[#5C6B62] mt-2">
                We'll send a 4-digit OTP to verify it.
              </p>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                    Your name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="w-full px-4 py-3 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none bg-white"
                    data-testid="signin-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className={`w-full px-4 py-3 rounded-xl border bg-white outline-none focus:ring-2 ${
                      email && !emailOk
                        ? "border-[#B23A48] focus:ring-red-100"
                        : "border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-[#EAF1ED]"
                    }`}
                    data-testid="signin-email-input"
                    required
                  />
                  {email && !emailOk && (
                    <div className="text-xs text-[#B23A48] mt-1">Enter a valid email (e.g. name@domain.com)</div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#1A231E] mb-1.5 uppercase tracking-wide">
                    Mobile number
                  </label>
                  <div className="flex items-center rounded-xl border border-[#E4E1D9] bg-white overflow-hidden focus-within:border-[#1E3F33] focus-within:ring-2 focus-within:ring-[#EAF1ED]">
                    <div className="px-4 py-3 bg-[#F7F6F2] border-r border-[#E4E1D9] font-semibold text-[#1A231E]">
                      🇮🇳 +91
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhone(e.target.value))}
                      placeholder="9876543210"
                      className="flex-1 px-4 py-3 outline-none text-[#1A231E]"
                      data-testid="signin-phone-input"
                      autoFocus
                      required
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || phone.length !== 10 || !name.trim() || !emailOk}
                className="btn-primary w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="signin-send-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                Send OTP
              </button>

              <p className="text-xs text-[#5C6B62] text-center mt-4">
                By continuing you agree to our{" "}
                <Link to="/terms" className="underline">Terms</Link> &{" "}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtp}>
              <button
                type="button"
                onClick={() => setStep("phone")}
                className="text-sm text-[#5C6B62] hover:text-[#1A231E] flex items-center gap-1 mb-4"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Change number
              </button>
              <div className="label-mono mb-2">Step 2 of 2</div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231E]">
                Enter the OTP
              </h1>
              <p className="text-sm text-[#5C6B62] mt-2">
                Sent via SMS to <span className="font-semibold text-[#1A231E]">+91 {phone}</span>
              </p>

              <input
                ref={otpRef}
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="• • • •"
                maxLength={4}
                className="w-full px-4 py-4 mt-5 rounded-xl border border-[#E4E1D9] focus:border-[#1E3F33] focus:ring-2 focus:ring-[#EAF1ED] outline-none bg-white text-center text-2xl font-mono tracking-[0.4em] font-bold"
                data-testid="signin-otp-input"
                autoComplete="one-time-code"
                required
              />

              <button
                type="submit"
                disabled={loading || otp.length !== 4}
                className="btn-primary w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="signin-verify-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Verify & continue
              </button>

              <button
                type="button"
                onClick={resendOtp}
                disabled={loading}
                className="block mx-auto mt-4 text-sm text-[#5C6B62] hover:text-[#1A231E] disabled:opacity-50"
                data-testid="signin-resend-otp-btn"
              >
                Didn't get it? <span className="text-[#C86646] font-semibold">Resend OTP</span>
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
